import React, { useState, useEffect, useCallback } from 'react';
import { MdCalculate, MdScience } from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';
import YsaFieldListPanel from './YsaFieldListPanel';
import { buildEffectiveFields } from '../../utils/ysaFields';

export default function YsaCalculateTab({ companyId, config, sensors }) {
    const { t } = useLanguage();
    const [models, setModels] = useState([]);
    const [modelId, setModelId] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [groupId, setGroupId] = useState('');
    const [values, setValues] = useState({});
    const [latestValues, setLatestValues] = useState({});
    const [calculating, setCalculating] = useState(false);
    const [results, setResults] = useState(null);

    const categories = config.categories || [];
    const activeCategory = categories.find(c => c._id === categoryId) || null;
    const groups = activeCategory?.groups || [];
    const activeGroup = groups.find(g => g._id === groupId) || null;
    const selectedModel = models.find(m => m._id === modelId) || null;
    const effectiveFields = activeGroup ? buildEffectiveFields(sensors, activeGroup) : [];
    const manualFields = effectiveFields.filter(f => f.type === 'manual');

    const fetchModels = useCallback(async () => {
        try {
            const res = await api.get(`/ysa/company/${companyId}/models`);
            setModels(res.data.data || []);
        } catch {
            toast.error(t('ysa.modelsLoadFailed'));
        }
    }, [companyId, t]);

    useEffect(() => { fetchModels(); }, [fetchModels]);

    useEffect(() => {
        setGroupId('');
    }, [categoryId]);

    useEffect(() => {
        setValues({});
        setResults(null);
        if (!activeGroup || sensors.length === 0) {
            setLatestValues({});
            return;
        }
        api.post('/sensor-data/bulk/latest', { uuids: sensors.map(s => s.sensorUUID) })
            .then(res => {
                const map = {};
                (res.data.data || []).forEach(item => { map[item.sensorUUID] = item; });
                setLatestValues(map);
            })
            .catch(() => {});
    }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sensors are shown here purely for reference (their latest reading) — the model
    // is only ever fed the manually entered fields, in group.fields order.
    const inputMismatch = selectedModel && activeGroup && selectedModel.inputSize !== manualFields.length;

    const handleCalculate = async () => {
        setResults(null);
        if (!selectedModel || !activeGroup) {
            toast.error(t('ysa.selectModelAndGroup'));
            return;
        }
        const missingManual = manualFields.some(f => values[f._id] === undefined || values[f._id] === '' || Number.isNaN(Number(values[f._id])));
        if (missingManual) {
            toast.error(t('ysa.fillAllInputsError'));
            return;
        }
        try {
            setCalculating(true);
            const inputs = manualFields.map(f => Number(values[f._id]));
            const res = await api.post(`/ysa/company/${companyId}/calculate`, { modelId, inputs });
            setResults(res.data.data.outputs);
        } catch (err) {
            toast.error(err.response?.data?.message || t('ysa.calculateFailed'));
        } finally {
            setCalculating(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="card" style={{ padding: 20 }}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 220px' }}>
                        <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>{t('ysa.selectModel')}</label>
                        <select className="form-select" value={modelId} onChange={e => { setModelId(e.target.value); setResults(null); }} style={{ width: '100%' }}>
                            <option value="">—</option>
                            {models.map(m => (
                                <option key={m._id} value={m._id}>{m.name} ({m.inputSize}→{m.outputSize})</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ flex: '1 1 220px' }}>
                        <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>{t('ysa.category')}</label>
                        <select className="form-select" value={categoryId} onChange={e => setCategoryId(e.target.value)} style={{ width: '100%' }}>
                            <option value="">—</option>
                            {categories.map(c => (
                                <option key={c._id} value={c._id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ flex: '1 1 220px' }}>
                        <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>{t('ysa.selectGroupForCalc')}</label>
                        <select className="form-select" value={groupId} onChange={e => setGroupId(e.target.value)} style={{ width: '100%' }} disabled={!activeCategory}>
                            <option value="">—</option>
                            {groups.map(g => (
                                <option key={g._id} value={g._id}>{g.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
                {inputMismatch && (
                    <div style={{ marginTop: 14, fontSize: 12, color: 'var(--warning, #d97706)', fontWeight: 600 }}>
                        {t('ysa.inputSizeMismatch', { expected: selectedModel.inputSize, got: manualFields.length })}
                    </div>
                )}
            </div>

            {activeGroup && (
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <YsaFieldListPanel
                        title={t('ysa.tabCalculate')}
                        icon={<MdScience />}
                        fields={effectiveFields}
                        latestValues={latestValues}
                        values={values}
                        onValueChange={(fid, val) => setValues(v => ({ ...v, [fid]: val }))}
                    />

                    <div className="card" style={{ padding: 20, flex: 1, minWidth: 280 }}>
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={handleCalculate}
                            disabled={calculating || !selectedModel}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}
                        >
                            {calculating ? <span className="spinner spinner-sm" /> : <MdCalculate />} {t('ysa.calculate')}
                        </button>

                        {results && (
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: 'var(--text-primary)' }}>{t('ysa.results')}</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {results.map((val, i) => (
                                        <div key={i} style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                                            padding: '10px 14px', borderRadius: 10,
                                            background: 'rgba(79,142,247,0.05)', border: '1px solid rgba(79,142,247,0.18)'
                                        }}>
                                            <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>
                                                {manualFields[i]?.name || t('ysa.output', { n: i + 1 })}
                                            </span>
                                            <span style={{ fontWeight: 700, fontFamily: 'var(--font-mono)', fontSize: 16, color: 'var(--primary)', whiteSpace: 'nowrap' }}>
                                                {typeof val === 'number' && !Number.isNaN(val) ? val.toFixed(6) : '—'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
